import { useParams } from 'react-router-dom';
import { Card, EmptyState, StatusChip } from '../components/atoms';
import { SectionHeader } from '../components/molecules';
import { AlertsTable, IssueList, MetricChartGrid, RangePicker, ReadingsBand } from '../components/organisms';
import { PageTemplate } from '../components/templates';
import { useAlerts, useClientStatus, useOverview, useSeries } from '../lib/api';
import { cn } from '../lib/cn';
import { clientIssues, HEALTH_CHIP, HEALTH_LABELS } from '../lib/metrics';
import { formatDateTime, formatRelative, resolveRange, useRangeSelection } from '../lib/time';

const OVERVIEW_CRUMB = { label: 'Fleet overview', to: '/' };

export function ClientPage() {
  const { clientId = '' } = useParams();
  const [range, setRange] = useRangeSelection();

  const overview = useOverview();
  const status = useClientStatus(clientId);
  const series = useSeries(clientId, range);
  const alerts = useAlerts(clientId, range);

  if (status.isPending || status.error) {
    return (
      <PageTemplate title={clientId} breadcrumb={[OVERVIEW_CRUMB, { label: clientId }]}>
        <Card raised>
          <EmptyState>{status.error ? `Could not load this client: ${status.error.message}` : 'Loading…'}</EmptyState>
        </Card>
      </PageTemplate>
    );
  }

  const { client, latest, health } = status.data;
  const pollMinutes = overview.data?.pollIntervalMinutes ?? 5;
  const pollIntervalMs = pollMinutes * 60_000;
  const issues = clientIssues(status.data, pollIntervalMs);
  const span = series.data
    ? { fromMs: Date.parse(series.data.from), toMs: Date.parse(series.data.to) }
    : resolveRange(range);

  return (
    <PageTemplate
      title={client.name}
      breadcrumb={[OVERVIEW_CRUMB, { label: client.name }]}
      badge={<StatusChip tone={HEALTH_CHIP[health].tone} label={HEALTH_LABELS[health].label} />}
      meta={
        <>
          <span className="font-mono">{client.id}</span>
          {client.ip && <span className="font-mono text-fg-muted"> ({client.ip})</span>} ·{' '}
          {latest ? (
            <span title={formatDateTime(latest.timestamp)}>last poll {formatRelative(latest.timestamp)}</span>
          ) : (
            'not polled yet'
          )}{' '}
          · polled every {pollMinutes} min
        </>
      }
      actions={<RangePicker range={range} onChange={setRange} />}
      hero={<ReadingsBand status={status.data} />}
    >
      <IssueList issues={issues} title="Open issues" allClear="All alert rules are within their limits." />

      <section className="flex flex-col gap-4">
        <SectionHeader
          title="History"
          description="Hover any chart to read every chart at that moment. Triangles on the time axis mark alerts sent."
        />
        <MetricChartGrid
          client={client}
          series={series.data}
          alerts={alerts.data ?? []}
          fromMs={span.fromMs}
          toMs={span.toMs}
          pollIntervalMs={pollIntervalMs}
          refreshing={series.isPlaceholderData}
        />
      </section>

      <section className={cn('flex flex-col gap-4', alerts.isPlaceholderData && 'opacity-55 transition-opacity')}>
        <SectionHeader title="Alerts sent" count={alerts.data?.length} description="Google Chat notifications in the selected range" />
        <Card className="overflow-hidden">
          {alerts.error ? (
            <EmptyState>Could not load alerts: {alerts.error.message}</EmptyState>
          ) : (
            <AlertsTable alerts={alerts.data ?? []} client={client} />
          )}
        </Card>
      </section>
    </PageTemplate>
  );
}
